import { useEffect, useRef, useState } from 'react'
import { listLegalEntities } from '../../api/legalEntities'
import { rsqlAnd, rsqlContains, rsqlEquals } from '../../lib/rsql'
import { Icon } from '../../components/icons/Icon'
import type { LegalEntityView } from '../../types/account'

export interface LegalEntityLookupFieldProps {
  id: string
  label: string
  value: { id: string; name: string } | null
  onChange: (value: { id: string; name: string } | null) => void
  /**
   * IB-326 — scope results to one Account's own Legal entities (the "New Case" wizard's Step 2:
   * a consultant should never cross-link a Case to a Legal entity outside the Account already
   * chosen in Step 1). Omitted callers (e.g. `BranchFormModal`) keep the unscoped, tenant-wide
   * search this field originally shipped with.
   */
  accountId?: string | undefined
}

/**
 * Minimal accessible combobox over `GET /legal-entities?filter=name==*term*`
 * — the `legalEntityId` picker on the New/Edit branch form. A sibling of
 * `AccountLookupField` (same pattern, IB-48 design §4.2) — the natural
 * generic `EntityLookupField` extraction is flagged in the UX report once a
 * third module needs it.
 */
export function LegalEntityLookupField({ id, label, value, onChange, accountId }: LegalEntityLookupFieldProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<LegalEntityView[]>([])
  const [loading, setLoading] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Debounced search — the timer only runs while the listbox is open, so a
  // closed/unfocused field never schedules background state updates.
  useEffect(() => {
    if (!open || query.trim().length < 1) {
      setResults([])
      return
    }
    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoading(true)
      const filter = rsqlAnd(accountId ? rsqlEquals('accountId', accountId) : null, rsqlContains('name', query.trim()))
      listLegalEntities({ filter, limit: 8 })
        .then((page) => {
          if (cancelled) return
          setResults(page.items)
        })
        .catch(() => {
          if (!cancelled) setResults([])
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [query, open, accountId])

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open])

  if (value) {
    return (
      <div className="iw-field">
        <span className="iw-field-label" id={`${id}-label`}>
          {label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="iw-pill iw-pill--neutral">{value.name}</span>
          <button type="button" className="iw-btn iw-btn--sm" onClick={() => onChange(null)}>
            Clear
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="iw-field" ref={containerRef} style={{ position: 'relative' }}>
      <label className="iw-field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        className="iw-input"
        placeholder="Search legal entities by name…"
        value={query}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
      />
      {open && (loading || results.length > 0 || query.trim().length > 0) ? (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label={`${label} results`}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 'var(--iw-z-dropdown)' as never,
            background: 'var(--iw-surface-raised)',
            border: '1px solid var(--iw-border)',
            borderRadius: 'var(--iw-radius)',
            boxShadow: 'var(--iw-shadow-lg)',
            margin: '4px 0 0',
            padding: 4,
            listStyle: 'none',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>Searching…</li>
          ) : results.length === 0 ? (
            <li style={{ padding: 8, fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>No matches</li>
          ) : (
            results.map((entity) => (
              <li key={entity.id} role="option" aria-selected="false">
                <button
                  type="button"
                  className="iw-btn iw-btn--ghost iw-btn--sm"
                  style={{ width: '100%', justifyContent: 'flex-start' }}
                  onClick={() => {
                    onChange({ id: entity.id, name: entity.name })
                    setQuery('')
                    setOpen(false)
                  }}
                >
                  <Icon name="landmark" size={13} />
                  {entity.name}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
